import { useEffect, useRef, useState } from "react";
import { RefreshCw, X, Link, Plus } from "lucide-react";
import type { MindsetAndroidVerificationChallengeStatus } from "@mindset/api-client";
import type { IntegrationAndroidClient } from "./integrationSetupClient";
import "./integrationAndroid.css";

interface Props { companyId: string; userId: string; applicationId: string; packages: string[]; origins: string[];
  client: IntegrationAndroidClient; onChanged?: () => void }
export function IntegrationAndroid(props: Props) {
  return <Android key={JSON.stringify([props.companyId, props.userId, props.applicationId])} {...props} />;
}
function Android({ companyId, userId, applicationId, packages, origins, client, onChanged }: Props) {
  const [rows, setRows] = useState<MindsetAndroidVerificationChallengeStatus[]>([]);
  const [packageName, setPackage] = useState(packages[0] ?? "");
  const [certificate, setCertificate] = useState("");
  const [origin, setOrigin] = useState(origins[0] ?? "");
  const [busy, setBusy] = useState(true), [error, setError] = useState(""), [status, setStatus] = useState("");
  const [recoveryFailed, setRecoveryFailed] = useState(false);
  const alive = useRef(false), running = useRef(false);
  useEffect(() => {
    const deadlines = rows.filter((row) => row.status === "PENDING").map((row) => Date.parse(row.expiresAt));
    if (!deadlines.length) return;
    const timer = window.setTimeout(() => {
      setRows((current) => current.map((row) => row.status === "PENDING" && Date.parse(row.expiresAt) <= Date.now()
        ? { ...row, status: "EXPIRED" } : row));
      setStatus("");
    }, Math.min(2147483647, Math.max(0, Math.min(...deadlines) - Date.now())));
    return () => window.clearTimeout(timer);
  }, [rows]);
  useEffect(() => {
    alive.current = true;
    let current = true;
    void client.listAndroidChallenges(companyId, userId, applicationId).then((value) => {
      if (current) { setRows(value); setRecoveryFailed(false); }
    }, () => { if (current) { setRecoveryFailed(true); setError("Android challenges could not be loaded."); } })
      .finally(() => { if (current) setBusy(false); });
    return () => { current = false; alive.current = false; };
  }, [client, companyId, userId, applicationId]);
  async function run(operation: () => Promise<void>) {
    if (running.current) return;
    running.current = true; setBusy(true); setError(""); setStatus("");
    try {
      await operation();
      if (!alive.current) return;
      const value = await client.listAndroidChallenges(companyId, userId, applicationId);
      if (alive.current) { setRows(value); setRecoveryFailed(false); onChanged?.(); }
    } catch { if (alive.current) { setRecoveryFailed(true); setError("Android setup could not be updated. Refresh before trying again."); } }
    finally { running.current = false; if (alive.current) setBusy(false); }
  }
  return <section className="integration-domain integration-android" aria-label="Android application verification">
    <header className="integration-applications-heading"><h3>Android verification</h3>
      <button type="button" className="portal-icon-button" title="Refresh Android challenges" aria-label="Refresh Android challenges" disabled={busy}
        onClick={() => void run(async () => {})}><RefreshCw size={17} /></button></header>
    <p role="status">{status || "Ownership verification pending. Native activation unavailable."}</p>
    {error ? <p role="alert">{error}</p> : null}
    <form className="integration-android-form" onSubmit={(event) => {
      event.preventDefault();
      if (busy || recoveryFailed) return;
      void run(async () => {
        await client.issueAndroidChallenge(companyId, userId, applicationId, packageName, certificate.trim().toUpperCase());
        if (alive.current) { setCertificate(""); setStatus("Challenge issued. Ownership verification pending."); }
      });
    }}>
      <label>Android package<select value={packageName} disabled={busy} onChange={(event) => setPackage(event.target.value)}>
        {packages.map((value) => <option key={value} value={value}>{value}</option>)}
      </select></label>
      <label>App-signing SHA-256<input value={certificate} disabled={busy} onChange={(event) => setCertificate(event.target.value)}
        required maxLength={95} autoComplete="off" spellCheck={false} pattern="([0-9a-fA-F]{2}:){31}[0-9a-fA-F]{2}" /></label>
      <button className="portal-button" disabled={busy || recoveryFailed || !packageName || !/^(?:[0-9A-F]{2}:){31}[0-9A-F]{2}$/i.test(certificate.trim())}>
        <Plus size={16} />Issue challenge</button>
    </form>
    <label>Association origin<select value={origin} disabled={busy || !origins.length} onChange={(event) => setOrigin(event.target.value)}>
      {!origins.length ? <option value="">No HTTPS origin registered</option> : origins.map((value) => <option key={value} value={value}>{value}</option>)}
    </select></label>
    <ul className="integration-application-list">{rows.map((row) => <li key={row.id}>
      <div style={{ overflowWrap: "anywhere", minWidth: 0 }}><strong>{row.packageName}</strong><p>{row.certificateSha256}</p>
        <span>{row.status} · {new Date(row.expiresAt).toLocaleString()}</span></div>
      <div className="integration-actions">
        <button type="button" className="portal-icon-button" title="Check domain association" aria-label={`Check association for ${row.packageName}`}
          disabled={busy || !origin || row.status !== "PENDING" || Date.parse(row.expiresAt) <= Date.now()} onClick={() => void run(async () => {
            await client.observeAndroidAssociation(companyId, userId, row.id, origin);
            if (alive.current) setStatus("Domain association matched. Ownership verification pending.");
          })}><Link size={17} /></button>
        <button type="button" className="portal-icon-button" title="Revoke challenge" aria-label={`Revoke challenge for ${row.packageName}`}
          disabled={busy || row.status === "REVOKED"} onClick={() => void run(async () => {
            await client.revokeAndroidChallenge(companyId, userId, row.id);
            if (alive.current) setStatus("Challenge revoked.");
          })}><X size={17} /></button>
      </div>
    </li>)}</ul>
  </section>;
}
